import {
  RELATIONSHIP_JOINED,
  RELATIONSHIP_PENDING_APPROVAL,
} from 'groups-common/src/lib/group-relationship-types';
import {GROUP_PRIVATE, GROUP_PUBLIC} from 'groups-common/src/lib/group-privacy-types';
import {MEMBER_PRIVACY_STATUS_PRIVATE, MEMBER_PRIVACY_STATUS_PUBLIC} from '../constants';
import {isLoggedIn, isPublic} from '../state/selectors/current-user-selectors';
import {
  getCurrentGroup,
  getGroupName,
  getPrivacyLevel,
  getGroupRelationship,
  getGroupId,
  getCoverImage,
  getImage,
  getMembersCount,
  getIsGroupAdmin,
} from '../state/selectors/current-group-selectors';
import {
  joinGroup,
  leaveGroup,
  requestJoinGroup,
  withdrawRequestJoinGroup,
  updateCoverImage,
  addMembersToGroup,
  updateGroup,
  configureGroupApps,
  archiveGroup,
} from '../state/actions/current-group-actions';
import {toggleActionsPopup} from '../state/actions/header-ui-actions';
import {isActionsPopupVisible} from '../state/selectors/header-ui-selectors';
import {createComponent} from '../lib/create-component';
import {
  openModalLeaveGroup,
  openModalArchiveGroup,
  openModalNotificationsSettings,
  openModalShareGroup,
  openModalAddMembers,
  openModalReportGroup,
  openModalEditGroup,
  startProxyActionAuth,
  startProxyActionMessagePopup,
} from '../lib/site-modals/site-modal';
import {setPrivacyStatus} from '../state/actions/current-user-actions';

const components = [
  createComponent({
    role: 'group-header-title',
    props: state => ({name: getGroupName(state)}),
    init: (textComp, {render}) => {
      render(({name}) => {
        textComp.text = name;
      });
    },
  }),
  createComponent({
    role: 'group-header-cover-img-upload-btn',
    props: state => ({groupId: getGroupId(state), isAdmin: getIsGroupAdmin(state)}),
    actions: {updateCoverImage},
    init: (btnComp, {actions, props, render}) => {
      render(({isAdmin}) => {
        if (isAdmin) {
          btnComp.expand();
        } else {
          btnComp.collapse();
        }
      });
      const uploadAndSetImg = () => {
        if (btnComp.value.length > 0) {
          btnComp.startUpload().then(({url}) => {
            actions.updateCoverImage(props.groupId, url);
            btnComp.reset();
          });
        }
      };

      btnComp.onChange(uploadAndSetImg);
    },
  }),
  createComponent({
    role: 'group-header-cover-img',
    props: state => ({coverImgUrl: getCoverImage(state)}),
    init: (imgComp, {render}) => {
      render(({coverImgUrl}) => {
        if (coverImgUrl) {
          imgComp.src = coverImgUrl;
        }
      });
    },
  }),
  createComponent({
    role: 'group-header-img',
    props: state => ({imgUrl: getImage(state)}),
    init: (imgComp, {render}) => {
      render(({imgUrl}) => {
        if (imgUrl) {
          imgComp.src = imgUrl;
        }
      });
    },
  }),
  createComponent({
    role: 'group-header-popup-btn',
    actions: {toggleActionsPopup},
    init: (comp, {actions}) => {
      comp.onClick(actions.toggleActionsPopup);
    },
  }),
  createComponent({
    role: 'group-header-add-members-img',
    props: state => ({
      relationship: getGroupRelationship(state),
      isAdmin: getIsGroupAdmin(state),
      groupId: getGroupId(state),
    }),
    actions: {openModalAddMembers, addMembersToGroup, startProxyActionMessagePopup},
    init: (comp, {actions, props, render}) => {
      render(({relationship, isAdmin}) => {
        if (relationship === RELATIONSHIP_JOINED && isAdmin) {
          comp.expand();
        } else {
          comp.collapse();
        }
      });

      comp.onClick(() =>
        actions.openModalAddMembers({groupId: props.groupId}).then(modalProps => {
          if (modalProps && modalProps.membersInviteList.length > 0) {
            const {membersInviteList, inviteByEmailList} = modalProps;
            console.log('Invite to group', {membersInviteList, inviteByEmailList});
            actions
              .addMembersToGroup(props.groupId, membersInviteList.map(item => item.siteMemberId))
              .then(() =>
                actions.startProxyActionMessagePopup({
                  message: props.t('message.success-add-members'),
                }),
              );
          }
        }),
      );
    },
  }),
  createComponent({
    role: 'group-header-popup-container',
    actions: {toggleActionsPopup},
    props: state => ({isActionsPopupVisible: isActionsPopupVisible(state)}),
    init: (containerComp, {actions, render}) => {
      render(({isActionsPopupVisible}) => {
        if (isActionsPopupVisible) {
          containerComp.expand();
        } else {
          containerComp.collapse();
        }
      });

      containerComp.onMouseOut(actions.toggleActionsPopup);
    },
  }),
  createComponent({
    role: 'group-header-members-count-text',
    props: state => ({
      membersCount: getMembersCount(state),
      privacyLevel: getPrivacyLevel(state),
    }),
    init: (textComp, {render}) => {
      render(({membersCount, privacyLevel, t}) => {
        const privacyLevelMap = {
          PUBLIC: t('group-privacy.public'),
          PRIVATE: t('group-privacy.private'),
          SECRET: t('group-privacy.secret'),
        };

        textComp.text = `${privacyLevelMap[privacyLevel]} · ${membersCount} ${t(
          membersCount === 1 ? 'single-member' : 'many-members',
        )}`;
      });
    },
  }),
  createComponent({
    role: {
      notificationsSettingsBtnComp: 'group-header-notifications-settings-btn',
      shareBtnComp: 'group-header-share-group-btn',
      leaveBtnComp: 'group-header-leave-group-btn',
      reportBtnComp: 'group-header-report-group-btn',
    },
    actions: {
      joinGroup,
      leaveGroup,
      openModalLeaveGroup,
      openModalNotificationsSettings,
      openModalShareGroup,
      openModalReportGroup,
      startProxyActionMessagePopup,
    },
    props: state => ({
      relationship: getGroupRelationship(state),
      groupId: getGroupId(state),
    }),
    init: (
      {notificationsSettingsBtnComp, shareBtnComp, leaveBtnComp, reportBtnComp},
      {render, actions, props},
    ) => {
      const onClickComp = (comp, cb) => comp.onClick(cb);
      render(({relationship}) => {
        if (relationship !== RELATIONSHIP_JOINED && leaveBtnComp) {
          leaveBtnComp.collapse();
        } else {
          leaveBtnComp.expand();
        }
      });
      onClickComp(notificationsSettingsBtnComp, actions.openModalNotificationsSettings);
      onClickComp(shareBtnComp, actions.openModalShareGroup);
      onClickComp(reportBtnComp, actions.openModalReportGroup);
      onClickComp(leaveBtnComp, () =>
        actions
          .openModalLeaveGroup()
          .then(leavingReason => leavingReason && actions.leaveGroup(props.groupId))
          .then(
            hasLeft =>
              hasLeft &&
              actions.startProxyActionMessagePopup({
                message: props.t('message.success-leave-group'),
              }),
          ),
      );
    },
  }),
  createComponent({
    role: 'group-header-actions-divider',
    props: state => ({isAdmin: getIsGroupAdmin(state)}),
    init: (dividerComp, {render}) => {
      render(({isAdmin}) => {
        if (isAdmin) {
          dividerComp.show();
        } else {
          dividerComp.hide();
        }
      });
    },
  }),
  createComponent({
    role: {
      editGroupBtnComp: 'group-header-edit-group-btn',
      archiveGroupBtnComp: 'group-header-archive-group-btn',
      editGroupImgComp: 'group-header-edit-group-img',
      archiveGroupImgComp: 'group-header-archive-group-img',
    },
    props: state => ({
      isAdmin: getIsGroupAdmin(state),
      groupId: getGroupId(state),
      group: getCurrentGroup(state),
    }),
    actions: {
      openModalArchiveGroup,
      openModalEditGroup,
      updateGroup,
      startProxyActionMessagePopup,
      configureGroupApps,
      archiveGroup,
    },
    init: (
      {editGroupBtnComp, archiveGroupBtnComp, editGroupImgComp, archiveGroupImgComp},
      {render, props, actions},
    ) => {
      const collapseComp = comp => comp && comp.collapse();
      const expandComp = comp => comp && comp.expand();
      const onClickComp = (comp, cb) => comp.onClick(cb);

      render(({isAdmin}) => {
        if (isAdmin) {
          expandComp(editGroupBtnComp);
          expandComp(editGroupImgComp);
          expandComp(archiveGroupBtnComp);
          expandComp(archiveGroupImgComp);
        } else {
          collapseComp(editGroupBtnComp);
          collapseComp(editGroupImgComp);
          collapseComp(archiveGroupBtnComp);
          collapseComp(archiveGroupImgComp);
        }
      });

      const archiveGroup = () =>
        actions
          .openModalArchiveGroup()
          .then(shouldArchive => shouldArchive && actions.archiveGroup(props.groupId))
          .then(() => props.wixApi.location.to('/'));

      const editGroup = () =>
        actions.openModalEditGroup({group: props.group}).then(result => {
          const {group, apps} = result || {};
          if (group) {
            actions.updateGroup(props.groupId, group).then(() =>
              actions.startProxyActionMessagePopup({
                message: 'Group updated successfully',
                timeout: 4000,
              }),
            );
          }
          if (apps) {
            actions
              .configureGroupApps(props.groupId, apps)
              .then(() => props.wixApi.location.to(props.wixApi.location.url));
          }
        });

      onClickComp(archiveGroupBtnComp, archiveGroup);
      onClickComp(archiveGroupImgComp, archiveGroup);
      onClickComp(editGroupBtnComp, editGroup);
      onClickComp(editGroupImgComp, editGroup);
    },
  }),
  createComponent({
    role: 'group-header-join-btn',
    props: state => ({
      isLoggedIn: isLoggedIn(state),
      isPublic: isPublic(state),
      groupId: getGroupId(state),
      privacyLevel: getPrivacyLevel(state),
      relationship: getGroupRelationship(state),
    }),
    actions: {
      joinGroup,
      leaveGroup,
      requestJoinGroup,
      withdrawRequestJoinGroup,
      startProxyActionAuth,
      startProxyActionMessagePopup,
      setPrivacyStatus,
    },
    init: (btnComp, {render, actions, props}) => {
      let action;
      render(({relationship, privacyLevel, isLoggedIn, isPublic, t}) => {
        if (relationship === RELATIONSHIP_JOINED) {
          action = () => {};
          btnComp.label = t('join-btn.joined');
          btnComp.disable();
        } else if (relationship === RELATIONSHIP_PENDING_APPROVAL) {
          action = groupId =>
            actions.withdrawRequestJoinGroup(groupId).then(() =>
              actions.startProxyActionMessagePopup({
                message: t('message.success-withdraw-request-join'),
              }),
            );
          btnComp.label = t('join-btn.withdraw-request');
          btnComp.enable();
        } else if (privacyLevel === GROUP_PRIVATE) {
          action = groupId =>
            actions.requestJoinGroup(groupId).then(() =>
              actions.startProxyActionMessagePopup({
                message: t('message.success-request-join'),
              }),
            );
          btnComp.label = t('join-btn.submit-request');
          btnComp.enable();
        } else if (privacyLevel === GROUP_PUBLIC) {
          action = groupId =>
            actions
              .joinGroup(groupId)
              .then(() =>
                actions.startProxyActionMessagePopup({message: t('message.success-join-group')}),
              );
          btnComp.label = t('join-btn.join');
          btnComp.enable();
        }

        if (!isLoggedIn) {
          action = () => {
            actions.startProxyActionAuth({type: 'auth/REQUEST_LOGIN'});
          };
        } else if (!isPublic) {
          action = () => {
            actions
              .startProxyActionAuth({type: 'auth/REQUEST_JOIN_COMMUNITY'})
              .then(joined =>
                actions.setPrivacyStatus(
                  joined ? MEMBER_PRIVACY_STATUS_PUBLIC : MEMBER_PRIVACY_STATUS_PRIVATE,
                ),
              );
          };
        }
      });

      btnComp.onClick(() => {
        if (action) {
          action(props.groupId);
        }
      });
    },
  }),
];

export function initHeader(controllerConfig, store, headerTranslations, wixApi) {
  if (controllerConfig.type === 'group-header') {
    components.forEach(c => c(controllerConfig, store, {t: headerTranslations, wixApi}));
  }
}
